import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { EditorContent, IProcessObject, IFileParentResource, IContextMenuState, IEmbedSubmission } from 'types'
import { findSection } from 'components/document/helpers/functions'
import { useApplicationStore } from 'hooks/application'
import {
  EditorState,
  RichUtils,
  convertToRaw,
  convertFromRaw,
  AtomicBlockUtils,
  ContentBlock,
  Modifier,
  getDefaultKeyBinding,
  DraftEditorCommand,
  SelectionState
} from 'draft-js'
import Editor, { composeDecorators } from '@draft-js-plugins/editor'
import createImagePlugin from '@draft-js-plugins/image'
import createVideoPlugin from '@draft-js-plugins/video'
import createFocusPlugin from '@draft-js-plugins/focus'
import createResizeablePlugin from './plugins/ResizeablePlugin'
import createLinkifyPlugin from '@draft-js-plugins/linkify'
import createAnchorPlugin from './plugins/AnchorPlugin'
import SmartLink from './components/SmartLink'
import ControlPanel from './components/ControlPanel'
import { selectedLink } from './components/Anchor'
import InsertMenu from './components/InsertMenu'
import { colorStyleFn } from './components/Highlighter'
import { useResolvedTheme } from 'hooks/useTheme'
import useUploadProgress from './hooks/useUploadProgress'

// Custom block components
import EmbeddedFile from './components/EmbeddedFile'
import EmbeddedImage from './components/EmbeddedImage'
import EmbeddedVideo from './components/EmbeddedVideo'
import EmbeddedIframe from './components/EmbeddedIframe'
import EmbeddedDashboard from './components/EmbeddedDashboard'
import EmbeddedSlicer from './components/EmbeddedSlicer'
import EmbeddedUploadProgress from './components/EmbeddedUploadProgress'
import api from 'helpers/api'

import ErrorBoundary from 'components/error'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import Menu from 'components/menu'
import {
  AddDashboardForm,
  DashboardEditData,
  DashboardEmbedData,
  DashboardView,
  seedFromView
} from './components/DashboardInsert'
import { AddIframeForm } from './components/IframeInsert'
import { AddSlicerForm } from './components/SlicerInsert'
import { EditorModeContext } from './EditorModeContext'
import { selectionInAtomicBlock } from './helpers/selection'
import {
  INSERT_CLASS,
  SELECTED_INSERT_CLASS,
  arrowDownFromInsert,
  arrowUpAroundInsert,
  deleteAroundInsert,
  findInsertAtClick,
  getSelectedInsertKey,
  insertLineAbove,
  insertLineAboveSelectedInsert,
  isCursorOnFirstLine,
  isLineAboveInsert,
  placeDomSelectionOnInsert,
  selectInsert
} from './helpers/lineAboveInsert'
import 'draft-js/dist/Draft.css'

// Initiate editor plugins
const focusPlugin = createFocusPlugin()
const resizeablePlugin = createResizeablePlugin({})
const anchorPlugin = createAnchorPlugin()
const linkifyPlugin = createLinkifyPlugin({
  component: SmartLink
})

const decorator = composeDecorators(resizeablePlugin.decorator, focusPlugin.decorator)
const imagePlugin = createImagePlugin({ decorator, imageComponent: EmbeddedImage })
const videoPlugin = createVideoPlugin({ decorator, videoComponent: EmbeddedVideo })
const { types } = videoPlugin

export const insertAtomicBlockWithoutExtraLine = (editorState: EditorState, entityKey: string): EditorState => {
  const selection = editorState.getSelection()
  const contentState = editorState.getCurrentContent()
  const currentBlock = contentState.getBlockForKey(selection.getStartKey())

  // If the current block is empty, replace it with the atomic block
  if (currentBlock.getText().length === 0 && currentBlock.getType() === 'unstyled') {
    // Insert using standard method first
    const withAtomic = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ')
    const newContent = withAtomic.getCurrentContent()
    const blockMap = newContent.getBlockMap()

    // Find and remove the empty block that was created before the atomic (blank line)
    const currentBlockKey = currentBlock.getKey()

    // Check if the original empty block still exists and remove it
    if (blockMap.has(currentBlockKey)) {
      const blockToRemove = blockMap.get(currentBlockKey)
      if (blockToRemove && blockToRemove.getText().length === 0 && blockToRemove.getType() === 'unstyled') {
        const newBlockMap = blockMap.delete(currentBlockKey)
        const newContentState = newContent.merge({
          blockMap: newBlockMap
        }) as typeof newContent
        return EditorState.push(withAtomic, newContentState, 'remove-range')
      }
    }

    return withAtomic
  }

  // If the current block has content, use the standard behavior
  return AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ')
}

// The form sets keys to undefined to mean "not set"; draft.js entity data must not carry them.
const withoutUndefined = <T extends object>(data: T): T =>
  Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined)) as T

// JSON with object keys in a fixed order, so two entity datas compare equal whatever order their keys
// were written in.
const canonicalJson = (value: unknown): string =>
  JSON.stringify(value, (_key, inner) =>
    inner && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(Object.entries(inner).sort(([a], [b]) => a.localeCompare(b)))
      : inner
  )

export const insertDashboard = (editorState: EditorState, data: DashboardEmbedData, entityKey?: string) => {
  const contentState = editorState.getCurrentContent()
  const views = data.views.map((view: DashboardView) => withoutUndefined(view))

  /* ---- UPDATE existing dashboard ---- */
  if (entityKey) {
    const existingData = contentState.getEntity(entityKey).getData()
    // Keys the form doesn't manage (convertedFromResponse, …) stay on the entity; keys it sets to undefined
    // (includeInDownload when it follows the section, a view's submissionId when submission is switched
    // off) are removed.
    const merged = withoutUndefined({ ...existingData, ...data, views })
    if (canonicalJson(merged) === canonicalJson(existingData)) return editorState

    const mutated = contentState.replaceEntityData(entityKey, merged)
    const cloned = convertFromRaw(convertToRaw(mutated))
    const withChange = EditorState.push(editorState, cloned, 'apply-entity')
    return EditorState.forceSelection(withChange, withChange.getSelection())
  }

  /* ---- INSERT brand-new dashboard ---- */
  if (selectionInAtomicBlock(editorState)) return editorState

  const withEntity = contentState.createEntity('DASHBOARD', 'IMMUTABLE', withoutUndefined({ ...data, views }))
  const newKey = withEntity.getLastCreatedEntityKey()
  return insertAtomicBlockWithoutExtraLine(editorState, newKey)
}

// How long after a click on a link to look again for its caret, if the editor hadn't taken it at once.
const LINK_SELECT_RETRY_MS = 60

// In a compact field, links are text to select and edit, not to open (the bubble opens them): no pointer, and no
// dragging them off as a browser drags a link, which moved the caret instead of selecting.
const COMPACT_LINKS = '[&_a]:!cursor-text [&_a]:[-webkit-user-drag:none]'

// The popovers opened from the compact field's formatting bubble: focus going into one is still editing.
const BUBBLE_POPOVERS = '#anchor-menu, #highlighter-menu'

export interface RichEditorProps {
  databaseDoc: any // TODO
  onChange?: (newContent: EditorContent) => void
  readOnly: boolean
  editorId: string
  resources: IFileParentResource[]
  border?: boolean
  className?: string
  process?: IProcessObject
  processSectionId?: string
  // Focus the editor (cursor at the end) once it is editable, then call onFocusedWhenEditable
  focusWhenEditable?: boolean
  onFocusedWhenEditable?: () => void
  /** Muted text shown while the editor is empty, as a field's placeholder. */
  placeholder?: string
  /**
   * For a short field in a panel rather than a document: no bar along the top, but the formatting in a bubble
   * over selected text, and a + at the field's foot for a photo, video or attachment.
   */
  compactToolbar?: boolean
}

export const RichEditor: React.FC<RichEditorProps> = ({
  databaseDoc,
  onChange,
  readOnly,
  editorId,
  resources,
  border,
  className,
  process,
  processSectionId,
  focusWhenEditable = false,
  onFocusedWhenEditable,
  placeholder,
  compactToolbar = false
}: RichEditorProps) => {
  'use no memo'

  const resolvedTheme = useResolvedTheme()
  const MAX_LIST_NESTING_DEPTH = 8

  const plugins = [focusPlugin, resizeablePlugin, imagePlugin, videoPlugin, anchorPlugin, linkifyPlugin]
  const { setSnackbarMessage, displayErrorMessage } = useApplicationStore()
  const [editorState, setEditorState] = useState<EditorState>()
  const [editorFocused, setEditorFocused] = useState<boolean>(false)
  const [menuState, setMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [editData, setEditData] = useState<DashboardEditData | undefined>()
  const [iframeMenuState, setIframeMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [iframeEditData, setIframeEditData] = useState<{ src: string; height: number; entityKey: string } | undefined>()
  const [slicerMenuState, setSlicerMenuState] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [slicerEditData, setSlicerEditData] = useState<
    | {
        tableId: string
        viewId: string
        columnId: string
        entityKey: string
      }
    | undefined
  >()
  const editorContainer = useRef<HTMLDivElement>(null)
  // The compact field's formatting bubble, placed over the selected text as it changes.
  const bubbleRef = useRef<HTMLDivElement>(null)
  const editor = useRef<Editor | null>(null)
  const dragCounter = useRef(0)
  const dropHandledByDraftJs = useRef(false)
  const [dragOver, setDragOver] = useState(false)
  const {
    insertUploadProgress,
    updateUploadProgress,
    replaceUploadProgressWithMedia,
    uploadProgressMap,
    editorStateRef
  } = useUploadProgress({
    editorState: editorState!,
    insertAtomicBlockWithoutExtraLine,
    videoEntityType: types.VIDEOTYPE
  })

  const uploadFiles = async (files: File[]) => {
    if (readOnly || !editorState) return

    // Phase 1: Insert all progress bars upfront so they all appear immediately
    const uploads: Array<{ file: File; mediaType: 'image' | 'video' | 'file'; entityKey: string }> = []

    for (const file of files) {
      const mediaType: 'image' | 'video' | 'file' = file.type.startsWith('image/')
        ? 'image'
        : file.type.startsWith('video/')
          ? 'video'
          : 'file'

      const currentState = editorStateRef.current ?? editorState
      const { editorState: newEditorState, entityKey } = insertUploadProgress(currentState, mediaType, file.name)
      editorStateRef.current = newEditorState
      handleOnChange(newEditorState)

      uploads.push({ file, mediaType, entityKey })
    }

    // Phase 2: Upload all files in parallel
    await Promise.all(
      uploads.map(async ({ file, mediaType, entityKey }) => {
        try {
          const response = await api.uploadFileWithProgress(file, resources, (progress) => {
            updateUploadProgress(entityKey, progress)
          })

          const finalEditorState = replaceUploadProgressWithMedia(entityKey, mediaType, {
            url: response.data.url,
            filename: response.data.filename
          })
          editorStateRef.current = finalEditorState
          handleOnChange(finalEditorState)
        } catch (err) {
          const cleanedState = replaceUploadProgressWithMedia(entityKey, mediaType, null)
          editorStateRef.current = cleanedState
          handleOnChange(cleanedState)
          displayErrorMessage(err)
        }
      })
    )
  }

  const blobsToFiles = (blobs: Blob[]): File[] => {
    const ts = Date.now()
    return blobs.map((blob, index) => {
      // Draft.js types drop/paste payloads as Blob[], but drag-and-drop items
      // are actually File objects with correct names. Preserve them as-is.
      if (blob instanceof File && blob.name) return blob
      // Genuine Blobs (e.g. clipboard screenshots) need a synthetic name
      const extension = blob.type.split('/')[1]?.split(';')[0] || 'png'
      const name = `pasted-${ts}-${index}.${extension}`
      return new File([blob], name, { type: blob.type })
    })
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) {
      setDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = Math.max(0, dragCounter.current - 1)
    if (dragCounter.current <= 0) {
      setDragOver(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setDragOver(false)

    // Skip if Draft.js already handled this drop
    if (dropHandledByDraftJs.current) {
      dropHandledByDraftJs.current = false
      return
    }

    if (e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files)
      uploadFiles(files)
    }
  }

  const setEditorRef = React.useCallback((inst: Editor | null) => {
    editor.current = inst
  }, [])

  useEffect(() => {
    if (readOnly) {
      setEditorFocused(false)
      setMenuState(INITIAL_CONTEXT_MENU_STATE)
      setIframeMenuState(INITIAL_CONTEXT_MENU_STATE)
      setSlicerMenuState(INITIAL_CONTEXT_MENU_STATE)
    }
  }, [readOnly])

  const editorReady = editorState !== undefined
  useEffect(() => {
    if (!focusWhenEditable || readOnly || !editorState) return
    setEditorState(EditorState.moveFocusToEnd(editorState))
    // Wait for Draft.js to render the forced selection before focusing the DOM node
    setTimeout(() => editor.current?.focus(), 0)
    onFocusedWhenEditable?.()
  }, [focusWhenEditable, readOnly, editorReady])

  const isValidDraftJsRawObject = (rawObject: any) => {
    if (!rawObject || typeof rawObject !== 'object') return false
    if (!Array.isArray(rawObject.blocks)) return false
    if (typeof rawObject.entityMap !== 'object') return false

    for (const block of rawObject.blocks) {
      if (typeof block.key !== 'string') return false
      if (typeof block.text !== 'string') return false
      if (typeof block.type !== 'string') return false
      if (typeof block.depth !== 'number') return false
      if (!Array.isArray(block.inlineStyleRanges)) return false
      if (!Array.isArray(block.entityRanges)) return false
      if (typeof block.data !== 'object') return false

      for (const range of block.inlineStyleRanges) {
        if (typeof range.offset !== 'number' || typeof range.length !== 'number' || typeof range.style !== 'string') {
          return false
        }
      }

      for (const range of block.entityRanges) {
        if (typeof range.offset !== 'number' || typeof range.length !== 'number' || typeof range.key !== 'number') {
          return false
        }
      }
    }

    // If all checks pass, return true
    return true
  }

  useEffect(() => {
    if (!editorFocused) {
      if (databaseDoc && databaseDoc.content && isValidDraftJsRawObject(databaseDoc.content)) {
        const content = convertFromRaw(databaseDoc.content)
        setEditorState(EditorState.createWithContent(content))
      } else setEditorState(EditorState.createEmpty())
    }
  }, [])

  const insertIframe = (editorState: EditorState, url: string, height: number, entityKey?: string) => {
    const contentState = editorState.getCurrentContent()

    /* ---- UPDATE existing iframe ---- */
    if (entityKey) {
      const mutated = contentState.mergeEntityData(entityKey, { src: url, height })
      const cloned = convertFromRaw(convertToRaw(mutated))
      const withChange = EditorState.push(editorState, cloned, 'apply-entity')
      return EditorState.forceSelection(withChange, withChange.getSelection())
    }

    /* ---- INSERT brand-new iframe ---- */
    if (RichUtils.getCurrentBlockType(editorState) === types.ATOMIC) {
      return editorState
    }
    const contentStateWithEntity = contentState.createEntity('IFRAME', 'IMMUTABLE', { src: url, height })
    const newKey = contentStateWithEntity.getLastCreatedEntityKey()
    return insertAtomicBlockWithoutExtraLine(editorState, newKey)
  }

  // The editor state as it is now, for work that finishes after an await or a drag: applying it to the
  // state captured when it started would overwrite the edits made in between.
  const getLiveEditorState = (): EditorState => (editorStateRef.current ?? editorState)!

  // Saves the height an embedded table, dashboard or iframe was dragged to. Reads the live editor state so a
  // resize released after other edits doesn't overwrite them.
  const resizeEmbed = (entityKey: string, height: number) => {
    const currentState = editorStateRef.current ?? editorState
    if (!currentState) return
    const mutated = currentState.getCurrentContent().mergeEntityData(entityKey, { height })
    const cloned = convertFromRaw(convertToRaw(mutated))
    const withChange = EditorState.push(currentState, cloned, 'apply-entity')
    handleOnChange(EditorState.forceSelection(withChange, withChange.getSelection()))
  }

  const insertSlicer = (
    editorState: EditorState,
    tableId: string,
    viewId: string,
    columnId: string,
    entityKey?: string
  ) => {
    const contentState = editorState.getCurrentContent()

    /* ---- UPDATE existing slicer ---- */
    if (entityKey) {
      const existingData = contentState.getEntity(entityKey).getData()
      if (existingData.tableId === tableId && existingData.viewId === viewId && existingData.columnId === columnId) {
        return editorState
      }
      const mutated = contentState.mergeEntityData(entityKey, { tableId, viewId, columnId })
      const cloned = convertFromRaw(convertToRaw(mutated))
      const withChange = EditorState.push(editorState, cloned, 'apply-entity')
      return EditorState.forceSelection(withChange, withChange.getSelection())
    }

    /* ---- INSERT brand-new slicer ---- */
    if (RichUtils.getCurrentBlockType(editorState) === types.ATOMIC) return editorState
    const withEntity = contentState.createEntity('SLICER', 'IMMUTABLE', { tableId, viewId, columnId })
    return insertAtomicBlockWithoutExtraLine(editorState, withEntity.getLastCreatedEntityKey())
  }

  const handleOnChange = (newEditorState: EditorState) => {
    // Trigger update when actual content updates https://github.com/facebook/draft-js/issues/2804
    const currentContent = editorState?.getCurrentContent()
    const newContent = newEditorState.getCurrentContent()

    // If only content selection changes don't trigger update
    if (editorState && editorState.getSelection() !== newEditorState.getSelection() && currentContent === newContent) {
      // Draft doesn't re-render blocks for a plain selection change, so force one to move the selected-insert outline
      const selectedInsertChanged = getSelectedInsertKey(editorState) !== getSelectedInsertKey(newEditorState)
      setEditorState(
        selectedInsertChanged
          ? EditorState.forceSelection(newEditorState, newEditorState.getSelection())
          : newEditorState
      )
      return
    }

    if (onChange) onChange({ content: convertToRaw(newContent) })
    setEditorState(newEditorState)
  }

  // Applies a state that may leave an insert selected. The browser's selection follows it onto the insert,
  // or Draft's next read of the native selection would move the cursor back to where it was.
  const handleInsertChange = (newState: EditorState) => {
    handleOnChange(newState)
    const insertKey = getSelectedInsertKey(newState)
    if (insertKey && editorContainer.current) placeDomSelectionOnInsert(editorContainer.current, insertKey)
  }

  const handleKeyCommand = (command: string, editorState: EditorState) => {
    const newState = deleteAroundInsert(editorState, command) ?? RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      handleInsertChange(newState)
      return 'handled'
    }
    return 'not-handled'
  }

  const handleReturn = (_e: React.KeyboardEvent, editorState: EditorState) => {
    const newState = insertLineAboveSelectedInsert(editorState)
    if (!newState) return 'not-handled'
    handleOnChange(newState)
    return 'handled'
  }

  // Typing while an insert is selected would write into the insert's block
  const handleBeforeInput = (_chars: string, editorState: EditorState) =>
    getSelectedInsertKey(editorState) ? 'handled' : 'not-handled'

  // Tables, dashboards, iframes and slicers get extra space around them while editing, and an outline while selected
  const blockStyleFn = (block: ContentBlock) => {
    if (!editorState || !isLineAboveInsert(editorState.getCurrentContent(), block)) return ''
    const selected = editorState.getSelection().getHasFocus() && block.getKey() === getSelectedInsertKey(editorState)
    return selected ? `${INSERT_CLASS} ${SELECTED_INSERT_CLASS}` : INSERT_CLASS
  }

  // Clicks on an insert fall through to the editor (its content takes no pointer events while editing), and the
  // browser would put the cursor in the nearest text. Instead, clicking an insert selects it, and clicking in the
  // gap above an insert with nothing editable above it gives the author a line to type on.
  const handleMouseDownOnInsert = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!editorState || (e.target as HTMLElement).closest('[data-block="true"]')) return
    const hit = findInsertAtClick(e.currentTarget, e.clientY, editorState.getCurrentContent())
    if (!hit) return
    e.preventDefault()
    const select = hit.action === 'select'
    handleOnChange(select ? selectInsert(editorState, hit.blockKey) : insertLineAbove(editorState, hit.blockKey))
    setTimeout(() => {
      editor.current?.focus()
      if (select && editorContainer.current) placeDomSelectionOnInsert(editorContainer.current, hit.blockKey)
    }, 0)
  }

  const keyBindingFn = (e: React.KeyboardEvent): DraftEditorCommand | null => {
    if (!editorState) return getDefaultKeyBinding(e)

    if (e.key === 'Tab') {
      // Stop the browser from moving focus / inserting a tab char
      e.preventDefault()

      const maxDepth = MAX_LIST_NESTING_DEPTH
      const newState = RichUtils.onTab(e, editorState, maxDepth)

      if (newState !== editorState) {
        handleOnChange(newState)
      }

      // Returning null: no further command handling
      return null
    }

    // The browser's cursor skips over inserts, so Up/Down arrows stop on them here instead
    const isPlainKey = !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey
    if (isPlainKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      const newState =
        e.key === 'ArrowUp' ? arrowUpAroundInsert(editorState, isCursorOnFirstLine()) : arrowDownFromInsert(editorState)
      if (newState) {
        e.preventDefault()
        handleInsertChange(newState)
        return null
      }
    }

    return getDefaultKeyBinding(e)
  }

  const toggleStyle = (controlType: string, styleType: string) => {
    if (editorState) {
      if (controlType === 'INLINE') {
        handleOnChange(RichUtils.toggleInlineStyle(editorState, styleType))
      } else {
        handleOnChange(RichUtils.toggleBlockType(editorState, styleType))
      }
      // Refocus editor after toggling style so user can continue typing
      setTimeout(() => editor.current?.focus(), 0)
    }
  }

  const myBlockRenderer = (contentBlock: ContentBlock) => {
    if (!editorState) return null
    if (contentBlock.getType() !== 'atomic') return null

    // Narrow try/catch to only the risky entity lookup — the React compiler cannot
    // optimise logical/optional-chain expressions (??  ?.  &&) inside try/catch.
    let entity: ReturnType<ReturnType<typeof editorState.getCurrentContent>['getEntity']> | null = null
    try {
      const block = contentBlock.getEntityAt(0)
      if (!block) return null
      entity = editorState.getCurrentContent().getEntity(block)
    } catch {
      return null
    }

    if (!entity) return null

    const entityType = entity.getType()
    const entityId = contentBlock.getEntityAt(0)

    if (entityType === 'FILE') {
      const data = entity.getData()
      return {
        component: EmbeddedFile,
        editable: false,
        props: { src: data.src, filename: data.filename }
      }
    }

    if (entityType === 'IFRAME') {
      const data = entity.getData()
      return {
        component: EmbeddedIframe,
        editable: false,
        props: {
          src: data.src,
          height: data.height,
          entityKey: entityId,
          openIframeDialog,
          onResizeHeight: resizeEmbed
        }
      }
    }

    if (entityType === 'DASHBOARD') {
      const data = entity.getData()
      return {
        component: EmbeddedDashboard,
        editable: false,
        props: {
          views: data.views,
          columns: data.columns,
          height: data.height,
          process: process,
          sectionId: processSectionId,
          entityKey: entityId,
          openDashboardDialog,
          onResizeHeight: resizeEmbed
        }
      }
    }

    if (entityType === 'SLICER') {
      const data = entity.getData()
      return {
        component: EmbeddedSlicer,
        editable: false,
        props: {
          tableId: data.tableId,
          viewId: data.viewId,
          columnId: data.columnId,
          entityKey: entityId,
          openSlicerDialog
        }
      }
    }

    if (entityType === 'UPLOAD_PROGRESS') {
      const data = entity.getData()
      const progressData = uploadProgressMap.get(entityId)
      return {
        component: EmbeddedUploadProgress,
        editable: false,
        props: {
          progress: progressData?.progress ?? data.progress ?? 0,
          mediaType: progressData?.mediaType ?? data.mediaType,
          filename: progressData?.filename ?? data.filename
        }
      }
    }

    return null
  }

  // The Edit Dashboard / Edit Iframe buttons sit at the foot of the embed, so their dialogs open to the
  // right of the button and grow upwards from its bottom edge — opening below would push the dialog
  // past the table and make the reader scroll to reach it.
  const besideEditButton = (anchorEl: HTMLElement): IContextMenuState => {
    const rect = anchorEl.getBoundingClientRect()
    return {
      open: true,
      top: 'auto',
      bottom: `${window.innerHeight - rect.bottom}px`,
      left: `${rect.right + 10}px`,
      right: 'auto'
    }
  }

  const openDashboardDialog = (anchorEl: HTMLElement, entityKey?: string) => {
    // re-use the existing DashboardInsert state handlers
    setMenuState(besideEditButton(anchorEl))

    // if invoked from ✏️, pre-seed the form with current entity data
    if (entityKey) {
      const content = editorState!.getCurrentContent()
      const entity = content.getEntity(entityKey)
      const data = entity.getData()
      const section = processSectionId && process ? findSection(processSectionId, process).section : null
      const submissions: IEmbedSubmission[] = section?.submissions ?? []

      setEditData({
        configs: data.views.map((v: DashboardView) => seedFromView(v, submissions)),
        columns: data.columns,
        height: data.height,
        includeInDownload: data.includeInDownload,
        entityKey
      })
    } else {
      setEditData(undefined)
    }
  }

  const openIframeDialog = (anchorEl: HTMLElement, entityKey: string) => {
    setIframeMenuState(besideEditButton(anchorEl))

    const content = editorState!.getCurrentContent()
    const entity = content.getEntity(entityKey)
    const data = entity.getData()

    setIframeEditData({
      src: data.src,
      height: data.height,
      entityKey
    })
  }

  const openSlicerDialog = (anchorEl: HTMLElement, entityKey: string) => {
    const menuWidth = 400
    const offset = 500

    setSlicerMenuState({
      open: true,
      top: `${anchorEl.getBoundingClientRect().bottom + 10}px`,
      left: `${anchorEl.getBoundingClientRect().left - menuWidth + offset}px`,
      right: 'auto',
      bottom: 'auto'
    })

    const content = editorState!.getCurrentContent()
    const data = content.getEntity(entityKey).getData()

    setSlicerEditData({
      tableId: data.tableId,
      viewId: data.viewId,
      columnId: data.columnId,
      entityKey
    })
  }

  // The compact field's bubble: while text is selected in it, over the selection, kept within the field's width.
  const showBubble =
    compactToolbar && !readOnly && editorFocused && !!editorState && !editorState.getSelection().isCollapsed()
  useLayoutEffect(() => {
    const bubble = bubbleRef.current
    const container = editorContainer.current
    const selection = window.getSelection()
    if (!showBubble || !bubble || !container || !selection || selection.rangeCount === 0) return
    // Typing in the link popover moves the page's selection there: the bubble stays over the text it formats.
    if (!container.contains(selection.anchorNode)) return
    const range = selection.getRangeAt(0).getBoundingClientRect()
    // A selection that has no box (as the editor hands focus to a popover) says nothing about where the text is.
    if (range.width === 0 && range.height === 0) return
    const box = container.getBoundingClientRect()
    const width = bubble.offsetWidth
    const centre = range.left - box.left + range.width / 2
    // No wider than the field: a long link wraps inside it rather than running off past it.
    bubble.style.maxWidth = `${box.width}px`
    bubble.style.left = `${Math.max(0, Math.min(centre - width / 2, box.width - width))}px`
    bubble.style.top = `${range.top - box.top}px`
  })
  // In a compact field, a click on a link selects the whole of it, so the bubble shows where it goes; a drag still
  // selects as much of it as is dragged over. Read once the editor has taken the click's selection.
  const latestState = useRef(editorState)
  latestState.current = editorState
  const selectLinkClicked = (): void => {
    // The click's caret reaches the editor's state a moment after the mouse is released, so it's looked for twice.
    const select = (): boolean => {
      const state = latestState.current
      if (!state || !state.getSelection().isCollapsed()) return false
      const link = selectedLink(state)
      if (link) handleOnChange(EditorState.forceSelection(state, link.selection))
      return !!link
    }
    window.setTimeout(() => {
      if (!select()) window.setTimeout(select, LINK_SELECT_RETRY_MS)
    }, 0)
  }

  // A compact field stops being edited when focus goes anywhere but into it or its bubble's popovers: the bubble
  // goes with it, even once a popover that kept it there has closed.
  useEffect(() => {
    if (!compactToolbar || !editorFocused) return
    const onFocusIn = (event: FocusEvent): void => {
      const target = event.target as HTMLElement | null
      if (!target || editorContainer.current?.contains(target) || target.closest?.(BUBBLE_POPOVERS)) return
      setEditorFocused(false)
    }
    document.addEventListener('focusin', onFocusIn)
    return () => document.removeEventListener('focusin', onFocusIn)
  }, [compactToolbar, editorFocused])

  if (!editorState) return null

  return (
    <ErrorBoundary>
      <EditorModeContext.Provider value={{ editorFocused, readOnly }}>
        <div
          key={`editor-${editorId}`}
          data-testid={`rich-editor-${editorId}`}
          className={`w-full rounded relative p-0 ${readOnly ? 'select-none' : 'select-all'}`}
          ref={editorContainer}
        >
          <div className="RichEditor-root editor">
            {!readOnly && editorFocused && !compactToolbar && (
              <ControlPanel
                editorState={editorState}
                getEditorState={getLiveEditorState}
                onToggle={toggleStyle}
                onChange={handleOnChange}
                addIframeModifier={insertIframe}
                processId={process?.publicId}
                sectionId={processSectionId}
                addDashboardModifier={insertDashboard}
                addSlicerModifier={insertSlicer}
                resources={resources}
                insertUploadProgress={insertUploadProgress}
                updateUploadProgress={updateUploadProgress}
                replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
              />
            )}
            {showBubble && (
              // Keeps the text selected while its controls are pressed.
              <div
                ref={bubbleRef}
                className="absolute z-[3] -translate-y-full pb-1.5"
                onMouseDown={(event) => event.preventDefault()}
              >
                <ControlPanel
                  variant="bubble"
                  editorState={editorState}
                  getEditorState={getLiveEditorState}
                  onToggle={toggleStyle}
                  onChange={handleOnChange}
                  addIframeModifier={insertIframe}
                  addDashboardModifier={insertDashboard}
                  addSlicerModifier={insertSlicer}
                  resources={resources}
                  insertUploadProgress={insertUploadProgress}
                  updateUploadProgress={updateUploadProgress}
                  replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
                />
              </div>
            )}
            <div
              className={`RichEditor-editor rounded relative ${readOnly ? '' : 'editor-editable editor-focused'} py-[11px] px-[5px] ${border ? 'border border-divider-strong' : 'border-none'} ${className || ''} ${!readOnly && dragOver ? 'editor-drag-over' : ''} ${compactToolbar && !readOnly ? COMPACT_LINKS : ''}`}
              onMouseUp={compactToolbar && !readOnly ? selectLinkClicked : undefined}
              // A click on a link in a compact field selects it rather than following it: the bubble opens it.
              onClick={
                compactToolbar && !readOnly
                  ? (event) => {
                      if ((event.target as HTMLElement).closest('a')) event.preventDefault()
                    }
                  : undefined
              }
              onDragEnter={readOnly ? undefined : handleDragEnter}
              onDragLeave={readOnly ? undefined : handleDragLeave}
              onDragOver={readOnly ? undefined : handleDragOver}
              onDrop={readOnly ? undefined : handleDrop}
              onMouseDown={readOnly ? undefined : handleMouseDownOnInsert}
            >
              {!readOnly && dragOver && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none rounded bg-really-light-blue">
                  <svg
                    className="w-10 h-10 text-blue mb-2"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <span className="text-[14px] font-medium text-blue">Drop files here to upload</span>
                </div>
              )}
              <Editor
                data-cy="editor"
                placeholder={placeholder}
                customStyleMap={colorStyleFn(resolvedTheme)}
                editorState={editorState}
                ref={setEditorRef}
                spellCheck={true}
                onFocus={() => setEditorFocused(true)}
                onBlur={(event: any) => {
                  // Don't lose focus if a menu is open or if clicking within the editor container
                  if (menuState.open || iframeMenuState.open || slicerMenuState.open) {
                    return
                  }
                  // Into the link or highlight popover opened from the formatting bubble: still editing.
                  if (event.relatedTarget?.closest?.(BUBBLE_POPOVERS)) return
                  if (editorContainer.current && !editorContainer.current.contains(event.relatedTarget)) {
                    setEditorFocused(false)
                  }
                }}
                plugins={plugins}
                handleKeyCommand={handleKeyCommand}
                handleReturn={handleReturn}
                handleBeforeInput={handleBeforeInput}
                blockStyleFn={blockStyleFn}
                keyBindingFn={keyBindingFn}
                onChange={handleOnChange}
                readOnly={readOnly}
                blockRendererFn={myBlockRenderer}
                handlePastedText={(text, html, editorState) => {
                  if (getSelectedInsertKey(editorState)) return 'handled'
                  // Defer rich pastes to Draft.js so images/styles aren't flattened to alt-text URLs.
                  if (html) return 'not-handled'
                  try {
                    const newState = Modifier.replaceText(
                      editorState.getCurrentContent(),
                      editorState.getSelection(),
                      text.trim()
                    )
                    handleOnChange(EditorState.push(editorState, newState, 'insert-fragment'))
                    return 'handled'
                  } catch {
                    setSnackbarMessage({
                      status: 'error',
                      message: "That text couldn't be pasted here. Click into the text where you want it and try again."
                    })
                    return 'not-handled'
                  }
                }}
                handlePastedFiles={(files: Blob[]) => {
                  if (readOnly) return 'not-handled' as const
                  const fileList = blobsToFiles(files)
                  if (fileList.length === 0) return 'not-handled' as const
                  uploadFiles(fileList)
                  return 'handled' as const
                }}
                handleDroppedFiles={(selection: SelectionState, files: Blob[]) => {
                  if (readOnly) return 'not-handled' as const
                  const fileList = blobsToFiles(files)
                  if (fileList.length === 0) {
                    return 'not-handled' as const
                  }
                  dropHandledByDraftJs.current = true
                  // Move cursor to drop location and sync the ref before uploading
                  const stateAtDropPoint = EditorState.forceSelection(editorStateRef.current ?? editorState!, selection)
                  editorStateRef.current = stateAtDropPoint
                  handleOnChange(stateAtDropPoint)
                  uploadFiles(fileList)
                  return 'handled' as const
                }}
              />
            </div>

            {compactToolbar && !readOnly && (
              <InsertMenu
                editorState={editorState!}
                onChange={handleOnChange}
                resources={resources}
                insertUploadProgress={insertUploadProgress}
                updateUploadProgress={updateUploadProgress}
                replaceUploadProgressWithMedia={replaceUploadProgressWithMedia}
              />
            )}

            {menuState.open && (
              <Menu
                id="dashboard-editor-menu"
                menuState={menuState}
                setMenuState={setMenuState}
                width={1000}
                zIndex={1000}
              >
                <AddDashboardForm
                  editorState={editorState}
                  getEditorState={getLiveEditorState}
                  onChange={handleOnChange}
                  onClose={() => setMenuState(INITIAL_CONTEXT_MENU_STATE)}
                  modifier={insertDashboard}
                  initialData={editData}
                  processId={process?.publicId}
                  sectionId={processSectionId}
                />
              </Menu>
            )}

            {iframeMenuState.open && (
              <Menu
                id="iframe-editor-menu"
                menuState={iframeMenuState}
                setMenuState={setIframeMenuState}
                width={500}
                zIndex={1000}
              >
                <AddIframeForm
                  editorState={editorState}
                  onChange={handleOnChange}
                  onClose={() => setIframeMenuState(INITIAL_CONTEXT_MENU_STATE)}
                  modifier={insertIframe}
                  initialData={iframeEditData}
                />
              </Menu>
            )}

            {slicerMenuState.open && (
              <Menu
                id="slicer-editor-menu"
                menuState={slicerMenuState}
                setMenuState={setSlicerMenuState}
                width={400}
                zIndex={1000}
              >
                <AddSlicerForm
                  editorState={editorState}
                  onChange={handleOnChange}
                  onClose={() => setSlicerMenuState(INITIAL_CONTEXT_MENU_STATE)}
                  modifier={insertSlicer}
                  initialData={slicerEditData}
                />
              </Menu>
            )}
          </div>
        </div>
      </EditorModeContext.Provider>
    </ErrorBoundary>
  )
}

export default RichEditor
